import { useMemo } from "react";
import { useIntl } from "react-intl";
import * as Yup from "yup";

export const FIELDS = {
  USERS: 'users',
  SMS_TEXT: 'smsText',
  TICKET_ID: 'ticketId',
  TICKET_NAME: 'ticketName',
}

const phoneRe = /^\+?\d{7,15}$/;
// eslint-disable-next-line no-useless-escape
const idCodeRe = /^[\w\-]+$/;

export const useValidationSchema = () => {
  const intl = useIntl();

  return useMemo(() => {
    return Yup.object().shape({
      [FIELDS.USERS]: Yup.string()
        .required(intl.formatMessage({ id: 'usersRequiredError' }))
        .test(
          'users-line-format',
          intl.formatMessage({ id: 'usersFormatError' }),
          function (value) {
            const { path, createError } = this;
            const lines = (value ?? "")
              .split('\n')
              .map(l => l.trim())
              .filter(Boolean);

            if (lines.length === 0) {
              return createError({
                path,
                message: intl.formatMessage({ id: 'usersRequiredError' }),
              });
            }

            const errors: string[] = [];

            lines.forEach((line, i) => {
              const rowIndex = i + 1;
              const parts = line.split(',').map(p => p.trim());

              if (parts.length < 3 || parts.length > 5) {
                errors.push(
                  intl.formatMessage(
                    { id: 'usersColumnsCountError' },
                    { n: rowIndex }
                  )
                );
                return;
              }

              const [phone, name, surname, count, idCode] = parts;

              if (!phone) {
                errors.push(intl.formatMessage({ id: 'usersPhoneRequiredError' }, { n: rowIndex }));
              } else if (!phoneRe.test(phone)) {
                errors.push(intl.formatMessage({ id: 'usersPhoneInvalidError' }, { n: rowIndex }));
              }

              if (!name) {
                errors.push(intl.formatMessage({ id: 'usersNameRequiredError' }, { n: rowIndex }));
              }

              if (!surname) {
                errors.push(intl.formatMessage({ id: 'usersSurnameRequiredError' }, { n: rowIndex }));
              }

              if (typeof count !== 'undefined' && count !== '') {
                if (!/^\d+$/.test(count) || Number(count) <= 0) {
                  errors.push(intl.formatMessage({ id: 'usersCountInvalidError' }, { n: rowIndex }));
                }
              }

              if (typeof idCode !== 'undefined' && idCode !== '') {
                if (!idCodeRe.test(idCode)) {
                  errors.push(intl.formatMessage({ id: 'usersIdCodeInvalidError' }, { n: rowIndex }));
                }
              }
            });

            if (errors.length) {
              return createError({
                path,
                message: errors.join('\n'),
              });
            }

            return true;
          }
        ),
        [FIELDS.SMS_TEXT]: Yup.string()
        .trim()
        .required(intl.formatMessage({ id: 'smsRequiredError' }))
        .test(
          'contains-link-token',
          intl.formatMessage({ id: 'smsTextMustContainTicketLinkError' }),
          (value) => value?.includes('%LINK_TO_TICKET%') ?? false
        ),
      [FIELDS.TICKET_ID]: Yup.number().optional().nullable(),
      [FIELDS.TICKET_NAME]: Yup.string().optional().nullable(),
    });
  }, [intl])
};